import React, { useMemo, useRef } from 'react';
import { useNotificationSender } from '@app/hooks/useNotificationSender';
import { AppMenuItem } from '@type/layout';
import { TieredMenu } from 'primereact/tieredmenu';
import { Tooltip } from 'primereact/tooltip';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const AppMenuSmall = ({ model }: { model: AppMenuItem[] }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const menuRefs = useRef<{ [key: string]: TieredMenu | null }>({});
  const { setLoadingConfirmedMessageList, setLoadingUnreadMessage } = useNotificationSender();

  /**
   * Check if a given route `to` matches the current pathname.
   * e.g. pathname = '/user/manage' → isActivePath('/user/manage') = true
   *      pathname = '/user/manage/detail' → isActivePath('/user/manage') = true (prefix match)
   */
  const isActivePath = (to?: string): boolean => {
    if (!to) return false;
    // Remove query params from pathname and to for comparison
    const cleanPath = pathname.split('?')[0];
    const cleanTo = to.split('?')[0];
    return cleanPath === cleanTo || cleanPath.startsWith(cleanTo + '/');
  };

  /**
   * Returns true if this menu item should be highlighted as active.
   * Priority order:
   * 1. item.activePaths[] — explicit list of URLs that activate this icon (most reliable)
   * 2. item.to — exact/prefix match against current pathname
   * 3. Any child item matches (recursive)
   */
  const isActiveItem = (item: any): boolean => {
    // 1. Explicit activePaths declared in the menu model
    if (Array.isArray(item.activePaths) && item.activePaths.length > 0) {
      return item.activePaths.some((p: string) => isActivePath(p));
    }
    // 2. Direct route match
    if (isActivePath(item.to)) return true;
    // 3. Recursive children check
    if (item.items && item.items.length > 0) {
      return item.items.some((child: any) => isActiveItem(child));
    }
    return false;
  };

  //  model -> format TieredMenu
  const convertToTieredMenuItems = (items: any[]): any[] => {
    const converted = items.map((item) => {
      if (item.separator) {
        return { separator: true };
      }
      const menuItem: any = {
        label: item.label,
        icon: item.iconImg,
        className: isActiveItem(item) ? 'active-route' : '',
        command: (e: any) => {
          if (item.disabled) {
            e.preventDefault();
            return;
          }
          navigate(item.to);
        },
      };

      if (item.items && item.items.length > 0) {
        menuItem.items = convertToTieredMenuItems(item.items);

        menuItem.template = (menuItemData: any, options: any) => {
          return (
            <a className={options.className} target={menuItemData.target} onClick={options.onClick}>
              {menuItemData.icon && <i className={menuItemData.icon}></i>}
              <span className="p-menuitem-text">{menuItemData.label}</span>
              <i className="pi pi-angle-right p-submenu-icon"></i>
            </a>
          );
        };
      }

      return menuItem;
    });
    return converted;
  };

  const rootItems = useMemo(() => model[0]?.items ?? [], [model]);

  const renderMenuItem = (item: any, i: number) => {
    if (item.separator && item.visible !== false) {
      return <li className="menu-separator" key={`sep-${i}`} />;
    }
    if (item.items && item.items.length > 0 && item.visible !== false) {
      const active = isActiveItem(item);
      return (
        <li key={i} className="layout-menuitem-small">
          <button
            className={`menu-trigger ${active ? 'menu-item-active' : ''} has-submenu`}
            data-active={active}
            data-route={item.to}
            data-label={item.label}
            title={item.label}
            data-pr-tooltip={item.label}
            data-pr-position="right"
            data-pr-classname="custom-tooltip"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const currentMenu = menuRefs.current[`menu-${i}`];
              Object.entries(menuRefs.current).forEach(([key, menuRef]) => {
                if (key !== `menu-${i}` && menuRef) {
                  const element = menuRef.getElement();
                  if (element && element.style.display !== 'none') {
                    menuRef.toggle(e);
                  }
                }
              });

              if (currentMenu) {
                currentMenu.toggle(e);
              }
            }}
          >
            {item.iconImg && <img src={item.iconImg} alt={item.label} className="layout-menuitem-icon" />}
            {/* arrow popup*/}
            <i className="pi pi-angle-right submenu-arrow"></i>
          </button>
          <TieredMenu
            ref={(el) => {
              menuRefs.current[`menu-${i}`] = el;
            }}
            model={convertToTieredMenuItems(item.items)}
            popup
            autoFocus={false}
            appendTo={document.body}
            className="tiered-menu-custom"
            onShow={() => {
              // Remove auto-focus from first menu item
              setTimeout(() => {
                const allPopups = document.querySelectorAll('.tiered-menu-custom');
                const lastPopup = allPopups[allPopups.length - 1];
                if (lastPopup && lastPopup instanceof HTMLElement) {
                  lastPopup.style.transform = 'translate(45px, -45px)';
                  lastPopup.style.position = 'absolute';
                  lastPopup.style.zIndex = '99';
                  // Remove all focus states set by PrimeReact internally
                  lastPopup.querySelectorAll('[data-p-focused="true"]').forEach((el) => {
                    (el as HTMLElement).setAttribute('data-p-focused', 'false');
                  });
                  lastPopup.querySelectorAll('.p-focus').forEach((el) => {
                    (el as HTMLElement).classList.remove('p-focus');
                  });
                  const focused = lastPopup.querySelector(':focus') as HTMLElement;
                  if (focused) focused.blur();
                }
              }, 0);
            }}
            pt={{
              root: {
                className: 'min-w-[200px] shadow-lg border rounded-lg z-[11]',
              },
              menu: {
                className: 'small-menu-panel p-0 m-0',
              },
              menuitem: {
                className: 'relative',
              },
              icon: {
                className: 'mr-2 text-gray-500 text-sm',
              },
              label: {
                className: 'flex-1',
              },
              submenuIcon: {
                className: 'ml-auto text-gray-400 text-xs',
              },
            }}
          />
        </li>
      );
    } else {
      if (!item.iconImg) return null;
      const active = isActiveItem(item);
      return item.to && !item.items && item.visible !== false ? (
        <li key={i} className="layout-menuitem-small">
          <Link
            to={item.to}
            title={item.label}
            data-pr-tooltip={item.label}
            data-pr-position="right"
            data-pr-classname="custom-tooltip"
            className={active ? 'menu-item-active' : ''}
            data-active={active}
            data-route={item.to}
            data-label={item.label}
            onClick={(e) => {
              if (item.disabled) {
                e.preventDefault();
              }
              // If the item has a 'to' property, it means it's a navigational link, so we set loading state for notifications
              if (item.to) {
                // Trigger loading state for both confirmed and unread messages when navigating to a new route
                setLoadingConfirmedMessageList?.(true);
                setLoadingUnreadMessage?.(true);
              }
            }}
          >
            <img src={item.iconImg} alt={item.label} className="layout-menuitem-icon" />
          </Link>
        </li>
      ) : null;
    }
  };

  return (
    <div className="layout-menu-small">
      <Tooltip target=".layout-menuitem-small [data-pr-tooltip]" />
      <ul className="layout-menu-small-list">{rootItems.map((item, i) => renderMenuItem(item, i))}</ul>
    </div>
  );
};

export default AppMenuSmall;
